/**
 * Ядро предметної моделі LearnKids.
 *
 * Головні інваріанти системи:
 *  1. Спроба (Attempt) — незмінний факт з ідентифікатором, згенерованим на клієнті.
 *     Сервер ніколи не приймає агрегати (бали, серії, статистику) від клієнта —
 *     усе похідне рахується з журналу спроб. Це робить «скидання прогресу»
 *     структурно неможливим.
 *  2. Кожне питання прив'язане до однієї навички (Skill) з дерева куррикулуму.
 *  3. Складність питання і рівень дитини живуть у спільній шкалі Elo,
 *     тому їх можна безпосередньо порівнювати.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Предмети та навички
// ─────────────────────────────────────────────────────────────────────────────

export const SUBJECT_IDS = ['math', 'ukrainian', 'english', 'science', 'logic'] as const;
export type SubjectId = (typeof SUBJECT_IDS)[number];

export interface Subject {
  id: SubjectId;
  /** Назва українською, як бачить дитина. */
  title: string;
  /** Коротке пояснення для батьківського кабінету. */
  description: string;
  /** Ключ у дизайн-системі (колір/іконка/бренд-персонаж). */
  theme: string;
  /** Ім'я фірмової іконки з apps/web/public/brand/icons. Емодзі не використовуємо. */
  icon: string;
}

/**
 * Клас НУШ. Використовується як «якір» віку, а не як жорсткий фільтр —
 * реальний рівень дитини визначає Elo.
 */
export type Grade = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Рівень за таксономією Блума — щоб банк не з'їхав у самі лише факти. */
export const COGNITIVE_LEVELS = ['recall', 'apply', 'analyze', 'create'] as const;
export type CognitiveLevel = (typeof COGNITIVE_LEVELS)[number];

/**
 * Skill (knowledge component) — атомарна одиниця, яку система оцінює окремо.
 * Дерево навичок — єдине джерело правди про «що ми взагалі вчимо».
 */
export interface Skill {
  /** Стабільний ієрархічний id: `math.arith.mul_table`. Ніколи не змінюється. */
  id: string;
  subject: SubjectId;
  /** Розділ усередині предмета: `arith`, `fractions`, `grammar`… */
  strand: string;
  title: string;
  /** Що саме дитина має вміти. Показується батькам у мапі прогресу. */
  description: string;
  /** Діапазон класів, для яких навичка доречна: [від, до]. */
  gradeBand: [Grade, Grade];
  /** Навички, які треба опанувати раніше. Використовується для розблокування. */
  prerequisites: string[];
  /** Стартова складність за шкалою Elo (див. ELO нижче). */
  baseDifficulty: number;
  /** Скільки правильних поспіль вважати «опанував» (для BKT-порогу). */
  masteryThreshold?: number;
  /** Id процедурних генераторів, які вміють робити питання для цієї навички. */
  generators?: string[];
  /** Чи це навичка-фундамент: її провал блокує все вище по дереву. */
  foundational?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Шкала складності
// ─────────────────────────────────────────────────────────────────────────────

export const ELO = {
  /** Рейтинг новачка у будь-якій навичці. */
  INITIAL: 1000,
  MIN: 400,
  MAX: 2400,
  /** K-фактор для дитини — швидка адаптація на старті, стабільність далі. */
  K_LEARNER_NEW: 40,
  K_LEARNER_SETTLED: 16,
  /** Після скількох спроб у навичці переходимо на «спокійний» K. */
  SETTLE_AFTER: 20,
  /** K-фактор для питання — рухається повільно, це калібрування банку. */
  K_ITEM: 8,
  /**
   * Цільова ймовірність успіху при доборі питань.
   * 0.75-0.85 — зона найближчого розвитку: достатньо складно, щоб вчитися,
   * достатньо легко, щоб не зневіритися. Для молодшої дитини беремо вище.
   */
  TARGET_SUCCESS: 0.8,
} as const;

/** Переводить різницю рейтингів у ймовірність правильної відповіді. */
export function expectedSuccess(learnerElo: number, itemElo: number): number {
  return 1 / (1 + Math.pow(10, (itemElo - learnerElo) / 400));
}

/** Складність питання, яка дає задану ймовірність успіху для цього рівня. */
export function eloForTargetSuccess(learnerElo: number, target: number): number {
  const clamped = Math.min(0.99, Math.max(0.01, target));
  return learnerElo - 400 * Math.log10(clamped / (1 - clamped));
}

// ─────────────────────────────────────────────────────────────────────────────
// Питання (Item)
// ─────────────────────────────────────────────────────────────────────────────

export const ITEM_FORMATS = [
  'mcq', // один правильний варіант із кількох
  'multi', // кілька правильних варіантів
  'numeric', // ввести число
  'text', // ввести слово/коротку відповідь
  'order', // розставити в правильному порядку
  'match', // зіставити пари
  'cloze', // вставити пропущене в текст
] as const;
export type ItemFormat = (typeof ITEM_FORMATS)[number];

export const ITEM_SOURCES = [
  'generator', // процедурний генератор — нескінченний, завжди коректний
  'bank', // курований банк, заведений вручну
  'llm', // згенеровано LLM, пройшло валідацію і схвалення батьком
] as const;
export type ItemSource = (typeof ITEM_SOURCES)[number];

export interface ItemChoice {
  id: string;
  text: string;
  /** Опційна підпис-картинка з бренд-паку. */
  image?: string;
}

/**
 * Питання у вигляді, в якому воно йде на клієнт.
 * ВАЖЛИВО: правильної відповіді тут немає — вона живе тільки на сервері,
 * інакше дитина знайде її у DevTools за 30 секунд.
 */
export interface ItemForLearner {
  /** Для процедурних: `gen:<generatorId>:<hash параметрів>`. Детермінований. */
  id: string;
  skillId: string;
  subject: SubjectId;
  format: ItemFormat;
  prompt: string;
  /** Умова задачі, яку варто показати окремо від запитання (текст для читання). */
  passage?: string;
  choices?: ItemChoice[];
  /** Підказки видаються по одній; кожна зменшує нарахування балів. */
  hintCount: number;
  /** Рекомендований час на відповідь, секунди. Розраховується з довжини й складності. */
  timeLimitSec: number;
  difficulty: number;
  /** Одиниця виміру для numeric-відповіді: «см», «грн». */
  unit?: string;
}

/** Повна форма питання — тільки на сервері та в інструментах наповнення банку. */
export interface Item extends ItemForLearner {
  source: ItemSource;
  cognitiveLevel: CognitiveLevel;
  gradeBand: [Grade, Grade];
  /** Правильна відповідь. Форма залежить від format. */
  answer: ItemAnswer;
  /** Пояснення показується ЗАВЖДИ після відповіді — це основна навчальна цінність. */
  explanation: string;
  hints: string[];
  /**
   * Нормалізований хеш змісту для виявлення дублікатів
   * (питання + відповідь, без пробілів і регістру).
   */
  contentHash: string;
  /** Емпірична статистика — заповнюється системою, калібрує difficulty. */
  stats?: ItemStats;
}

export type ItemAnswer =
  | { kind: 'choice'; choiceId: string }
  | { kind: 'choices'; choiceIds: string[] }
  | { kind: 'numeric'; value: number; tolerance?: number }
  | { kind: 'text'; accepted: string[]; caseSensitive?: boolean }
  | { kind: 'order'; sequence: string[] }
  | { kind: 'match'; pairs: Array<[string, string]> };

export interface ItemStats {
  timesServed: number;
  timesCorrect: number;
  avgResponseMs: number;
  /** Частка правильних. Якщо різко розходиться з difficulty — питання на ревізію. */
  pValue: number;
  lastServedAt?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Учень і його стан
// ─────────────────────────────────────────────────────────────────────────────

export interface Learner {
  id: string;
  displayName: string;
  /** Рік/місяць народження — щоб клас перераховувався автоматично. */
  birthDate: string;
  grade: Grade;
  avatarKey: string;
  /** Персональні коефіцієнти складності, якщо батько хоче підкрутити вручну. */
  targetSuccess: number;
  createdAt: string;
}

/**
 * Стан однієї навички для однієї дитини.
 * Це і є «модель учня» — з неї рахується все: добір питань, звіти, слабкі місця.
 */
export interface SkillState {
  learnerId: string;
  skillId: string;
  /** Рейтинг за шкалою Elo. */
  elo: number;
  /** Ймовірність, що навичка вже опанована (Bayesian Knowledge Tracing). */
  pKnown: number;
  attempts: number;
  correct: number;
  /** Поточна серія правильних відповідей у цій навичці. */
  streak: number;
  /** Параметри інтервального повторення (FSRS-подібні). */
  stability: number;
  retrievability: number;
  lapses: number;
  reps: number;
  /** Коли навичку варто повторити. Прострочені мають найвищий пріоритет. */
  dueAt: string | null;
  lastAttemptAt: string | null;
  /** Похідна оцінка 0..1 для UI: мапа прогресу, «слабкі місця». */
  mastery: number;
}

/** Параметри BKT. Підібрані під короткі сесії дитячого застосунку. */
export const BKT = {
  /** Апріорна ймовірність, що дитина вже знає навичку. */
  P_INIT: 0.25,
  /** Ймовірність вивчити з однієї вдалої спроби. */
  P_TRANSIT: 0.14,
  /** Ймовірність помилитися, знаючи (описка, поспіх). */
  P_SLIP: 0.1,
  /** Ймовірність вгадати, не знаючи. Перекривається реальною к-стю варіантів. */
  P_GUESS: 0.2,
  /** Поріг, з якого вважаємо навичку опанованою. */
  MASTERED: 0.95,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Сесія та спроби
// ─────────────────────────────────────────────────────────────────────────────

export const SESSION_MODES = [
  'daily', // основний режим: адаптивний мікс за планом дня
  'practice', // тренування конкретного предмета за вибором дитини
  'boss', // «бос» — контрольна по слабких навичках, більша нагорода
  'review', // тільки прострочені повторення
  'duel', // однаковий набір питань для двох дітей, порівняння результату
] as const;
export type SessionMode = (typeof SESSION_MODES)[number];

export interface QuizSession {
  id: string;
  learnerId: string;
  mode: SessionMode;
  /** Скільки питань заплановано. */
  plannedCount: number;
  /** Розподіл питань по предметах, вирішений на старті сесії. */
  blueprint: Array<{ subject: SubjectId; count: number }>;
  startedAt: string;
  endedAt: string | null;
  /** Нараховано за сесію. Похідне від спроб, зберігається для швидкого показу. */
  xpEarned: number;
}

/**
 * Спроба — незмінний факт. Id генерує КЛІЄНТ (UUIDv7) до відправки на сервер,
 * тому повторна відправка тієї самої спроби ідемпотентна
 * (`ON CONFLICT (id) DO NOTHING`). Саме це гарантує, що результат дитини
 * не загубиться навіть при обриві мережі посеред відповіді.
 */
export interface Attempt {
  id: string;
  sessionId: string;
  learnerId: string;
  itemId: string;
  skillId: string;
  /** Що саме відповіла дитина — для роботи над помилками. */
  response: unknown;
  isCorrect: boolean;
  responseMs: number;
  hintsUsed: number;
  /** Час за годинником клієнта — може брехати, але корисний для діагностики. */
  clientTs: string;
  /** Час, коли сервер прийняв спробу. Це і є авторитетний час. */
  serverTs: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Прогресія та мотивація
// ─────────────────────────────────────────────────────────────────────────────

/** XP за спробу. Складність і швидкість винагороджуються, підказки — здешевлюють. */
export const XP = {
  BASE_CORRECT: 10,
  /** Множник за складність: питання рівня дитини +0, значно складніше — до +100%. */
  DIFFICULTY_BONUS_MAX: 10,
  /** Бонус за відповідь швидше половини ліміту. */
  SPEED_BONUS: 3,
  /** Скільки XP знімає кожна використана підказка. */
  HINT_PENALTY: 3,
  /** Серія правильних: множник росте до x2 на 10-й поспіль. */
  STREAK_MAX_MULTIPLIER: 2,
  /** Бонус за виправлену раніше помилку — головна нагорода в системі. */
  REDEMPTION_BONUS: 15,
} as const;

export interface TrophyTier {
  key: string;
  title: string;
  minTrophies: number;
  /** Файл бренд-паку, який показуємо на цьому ранзі. */
  badge: string;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  /** Прихована — показується тільки після відкриття. */
  secret?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Батьківський контроль
// ─────────────────────────────────────────────────────────────────────────────

export interface ScheduleWindow {
  /** 0 = неділя … 6 = субота. */
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface ParentalControls {
  learnerId: string;
  /** Дозволені вікна доступу. Порожній масив = без обмежень. */
  schedule: ScheduleWindow[];
  /** Ліміт хвилин на добу. */
  dailyMinutesLimit: number;
  /** Скільки хвилин безперервно, після чого примусова перерва. */
  sessionMinutes: number;
  breakMinutes: number;
  /** Мінімум питань на день, щоб зарахувати «денну ціль». */
  dailyGoalItems: number;
  /** Предмети, увімкнені для цієї дитини. */
  enabledSubjects: SubjectId[];
  /** Ручне підсилення предмета батьком поверх автоматичного. 1 = без змін. */
  subjectWeightOverride: Partial<Record<SubjectId, number>>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ролі
// ─────────────────────────────────────────────────────────────────────────────

/**
 * viewer — «лише подивитись»: заходить кодом у тому ж полі, що й діти, бачить
 * статистику тільки тих дітей, до яких його привʼязали, і нічого не змінює.
 * Окрема роль, а не батько з прапорцем: кожна перевірка `requireParent`
 * (гаманець, виплати, ліміти, модерація) відмовляє йому сама, без правок.
 */
export const USER_ROLES = ['parent', 'child', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];
